import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { 
  Search, 
  ChevronRight,
  Users,
  Upload,
  Filter
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

const staffCategories = [
  { value: "all", label: "All Staff" },
  { value: "pool", label: "Pool" },
  { value: "skippers", label: "Skippers" },
  { value: "mariner", label: "Mariner" },
  { value: "navigator", label: "Navigator" },
  { value: "boating", label: "Boating" },
  { value: "health", label: "Health Center" },
  { value: "supervisor", label: "Supervisors" },
  { value: "transportation", label: "Transportation" },
  { value: "office", label: "Office" },
  { value: "minor", label: "Minors (15-17)" },
];

function getCertStatus(staff, certType) {
  const cert = staff.certifications?.[certType];
  
  // CEU supersedes CPR: if CEU is valid, CPR is considered valid
  if ((certType === "cpr_aed" || certType === "cpr_aed_rte_combo") && staff.certifications) {
    const ceu = staff.certifications["ceu"];
    if (ceu?.has_cert && ceu.expiration_date) {
      const ceuExpDate = new Date(ceu.expiration_date);
      const now = new Date();
      const ceuDaysUntil = Math.floor((ceuExpDate - now) / (1000 * 60 * 60 * 24));
      if (ceuDaysUntil >= 0) {
        if (ceuDaysUntil <= 60) return "expiring";
        return "active";
      }
    }
  }

  if (!cert?.has_cert) return "none";
  
  if (!cert.expiration_date) return "active";
  
  const expDate = new Date(cert.expiration_date);
  const now = new Date();
  const daysUntil = Math.floor((expDate - now) / (1000 * 60 * 60 * 24));
  
  if (daysUntil < 0) return "expired";
  if (daysUntil <= 60) return "expiring";
  return "active";
}

function StatusBadge({ status }) {
  const styles = {
    none: "bg-slate-100 text-slate-500 border-slate-200",
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    expiring: "bg-amber-100 text-amber-800 border-amber-300",
    expired: "bg-red-100 text-red-800 border-red-300",
  };
  
  const labels = {
    none: "None",
    active: "Valid",
    expiring: "Expiring",
    expired: "Expired",
  };
  
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

export default function StaffListPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [staffList, setStaffList] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [divisionFilter, setDivisionFilter] = useState("all");
  const [campStart, setCampStart] = useState("2026-06-29");

  // Compute age at camp_start (returns null if no DOB)
  const ageAtCamp = (dob) => {
    if (!dob) return null;
    const d = new Date(dob);
    const ref = new Date(campStart);
    if (isNaN(d.getTime()) || isNaN(ref.getTime())) return null;
    let age = ref.getFullYear() - d.getFullYear();
    const m = ref.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && ref.getDate() < d.getDate())) age--;
    return age;
  };

  const isWorkingPapers = (staff) => {
    const a = ageAtCamp(staff.date_of_birth);
    return a !== null && a >= 15 && a <= 17;
  };

  // Group label: "Working Papers" for 15-17yo, else their division
  const getGroupLabel = (staff) => isWorkingPapers(staff) ? "Working Papers" : (staff.division || "No Division");

  const fetchStaff = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (categoryFilter !== "all") params.append("category", categoryFilter);
      if (divisionFilter !== "all") params.append("division", divisionFilter);
      
      const response = await axios.get(`${API}/staff?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaffList(response.data);
    } catch (error) {
      toast.error("Failed to load staff list");
    } finally {
      setLoading(false);
    }
  };

  const fetchDivisions = async () => {
    try {
      const response = await axios.get(`${API}/divisions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDivisions(response.data);
    } catch (error) {
      console.error("Failed to load divisions");
    }
  };

  const fetchCampStart = async () => {
    try {
      const response = await axios.get(`${API}/settings/camp-dates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.camp_start) setCampStart(response.data.camp_start);
    } catch (error) {
      // keep default
    }
  };

  useEffect(() => {
    fetchStaff();
    fetchDivisions();
    fetchCampStart();
  }, [token]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchStaff();
    }, 300);
    return () => clearTimeout(debounce);
  }, [search, categoryFilter, divisionFilter]);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            Staff List
          </h1>
          <p className="text-slate-500 mt-1">
            {staffList.length} staff member{staffList.length !== 1 ? "s" : ""} (2026 Session)
          </p>
        </div>
        
        <Button
          onClick={() => navigate("/bulk-upload")}
          variant="outline"
          className="border-brand-navy text-brand-navy hover:bg-brand-navy/5"
          data-testid="bulk-upload-button"
        >
          <Upload className="h-4 w-4 mr-2" />
          Bulk Upload
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name or position..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="staff-search-input"
            />
          </div>
          <div className="flex gap-3">
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-40" data-testid="category-filter">
                <Filter className="h-4 w-4 mr-2 text-slate-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {staffCategories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={divisionFilter} onValueChange={setDivisionFilter}>
              <SelectTrigger className="w-40" data-testid="division-filter">
                <SelectValue placeholder="Division" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Divisions</SelectItem>
                {divisions.map((div) => (
                  <SelectItem key={div} value={div}>
                    {div}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Staff Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-navy"></div>
          </div>
        ) : staffList.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center mb-4">
              <Users className="h-6 w-6 text-slate-400" />
            </div>
            <h3 className="font-heading font-semibold text-slate-900 mb-1">
              No staff found
            </h3>
            <p className="text-sm text-slate-500">
              Try adjusting your filters or sync with CampMinder
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader className="bg-slate-50/80 sticky top-0">
              <TableRow>
                <TableHead className="font-semibold">Name</TableHead>
                <TableHead className="font-semibold">Position</TableHead>
                <TableHead className="font-semibold">Division</TableHead>
                <TableHead className="font-semibold text-center">CPR/AED</TableHead>
                <TableHead className="font-semibold text-center">Lifeguard</TableHead>
                <TableHead className="font-semibold text-center">WSI</TableHead>
                <TableHead className="font-semibold text-center">Health</TableHead>
                <TableHead className="font-semibold text-center">Boating</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(() => {
                // Re-sort so Working Papers (15-17yo) form a separate section at the top
                const sorted = [...staffList].sort((a, b) => {
                  const aWP = isWorkingPapers(a);
                  const bWP = isWorkingPapers(b);
                  if (aWP !== bWP) return aWP ? -1 : 1; // WP first
                  // Within WP group: sort by last name
                  if (aWP && bWP) {
                    return (a.last_name || "").localeCompare(b.last_name || "");
                  }
                  // Otherwise: sort by division then last name
                  const divCmp = (a.division || "").localeCompare(b.division || "");
                  if (divCmp !== 0) return divCmp;
                  return (a.last_name || "").localeCompare(b.last_name || "");
                });
                return sorted.map((staff, index) => {
                  const currentGroup = getGroupLabel(staff);
                  const prevGroup = index > 0 ? getGroupLabel(sorted[index - 1]) : null;
                  const showDivisionHeader = currentGroup !== prevGroup;
                  const isWP = currentGroup === "Working Papers";

                return (
                  <React.Fragment key={staff.id}>
                    {showDivisionHeader && (
                      <TableRow className={`${isWP ? "bg-amber-50" : "bg-slate-100/80"} pointer-events-none`} data-testid={`division-header-${currentGroup}`}>
                        <TableCell colSpan={9} className="py-2 px-4">
                          <span className={`text-xs font-bold uppercase tracking-wider ${isWP ? "text-amber-800" : "text-slate-500"}`}>
                            {isWP ? "Working Papers (Ages 15-17)" : currentGroup}
                          </span>
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow
                      className="table-row-hover cursor-pointer"
                      onClick={() => navigate(`/staff/${staff.id}`)}
                      style={{ animationDelay: `${index * 30}ms` }}
                      data-testid={`staff-row-${staff.id}`}
                    >
                      <TableCell className="font-medium">
                        {staff.last_name && staff.first_name
                          ? `${staff.last_name}, ${staff.first_name}`
                          : staff.name}
                      </TableCell>
                      <TableCell className="text-slate-600">{staff.position || "—"}</TableCell>
                      <TableCell className="text-slate-600">{staff.division || "—"}</TableCell>
                      <TableCell className="text-center">
                        <StatusBadge status={getCertStatus(staff, "cpr_aed")} />
                      </TableCell>
                      <TableCell className="text-center">
                        <StatusBadge status={getCertStatus(staff, "lifeguard")} />
                      </TableCell>
                      <TableCell className="text-center">
                        <StatusBadge status={getCertStatus(staff, "wsi")} />
                      </TableCell>
                      <TableCell className="text-center">
                        <StatusBadge status={getCertStatus(staff, "health_nursing")} />
                      </TableCell>
                      <TableCell className="text-center">
                        <StatusBadge status={getCertStatus(staff, "boating")} />
                      </TableCell>
                      <TableCell>
                        <ChevronRight className="h-4 w-4 text-slate-400" />
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
                });
              })()}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
